import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';
import map from 'lodash/map';

import { ParticipantFieldScope } from '../__generated__/graphql';

interface Event {
  ticket_categories: {
    id: string;
    tickets: {
      id: string;
      time_slots: {
        id: string;
      }[];
      promotions: {
        id: string;
      }[];
    }[];
  }[];
  products: {
    id: string;
    product_variants: {
      id: string;
    }[];
    promotions: {
      id: string;
    }[];
  }[];
  participant_fields: {
    id: string;
    scope: ParticipantFieldScope;
    choices: {
      id: string;
    }[];
    tickets: {
      id: string;
    }[];
    products: {
      id: string;
    }[];
  }[];
}

interface FilterParams {
  ticket_categories?: string[];
  tickets?: string[];
  time_slots?: string[];
  products?: string[];
  product_variants?: string[];
  promotions?: string[];
  participant_fields?: { id: string; }[];
  participant_field_choices?: string[];
}

export const syncTimeSlots = (filters: FilterParams, event?: Event | null) => {
  const possibleTickets = event?.ticket_categories.reduce(
    (tickets, ticketCategory) => [...tickets, ...ticketCategory.tickets],
    [],
  ) || [];
  const possibleTicketIds = map(possibleTickets, 'id');
  const newTicketIds = intersection((filters?.tickets || []), possibleTicketIds);

  const possibleTimeSlotIds = map(flatten(map(possibleTickets.filter((ticket) => (
    (filters?.tickets || []).includes(ticket.id)
  )), 'time_slots')), 'id');

  const newTimeSlotIds = intersection((filters?.time_slots || []), possibleTimeSlotIds);

  if (newTicketIds.join() !== (filters?.tickets || []).join()
                || newTimeSlotIds.join() !== (filters?.time_slots || []).join()) {
    return {
      ...filters,
      tickets: newTicketIds,
      time_slots: newTimeSlotIds,
    };
  }

  return filters;
};

export const syncProductVariants = (filters: FilterParams, event?: Event | null) => {
  const possibleProducts = event?.products || [];
  const possibleProductIds = map(possibleProducts, 'id');
  const newProductIds = intersection((filters?.products || []), possibleProductIds);

  const possibleProductVariantIds = map(flatten(map(possibleProducts.filter((product) => (
    (filters?.products || []).includes(product.id)
  )), 'product_variants')), 'id');

  const newProductVariantIds = intersection((filters?.product_variants || []), possibleProductVariantIds);

  if (newProductIds.join() !== (filters?.products || []).join()
            || newProductVariantIds.join() !== (filters?.product_variants || []).join()) {
    return {
      ...filters,
      products: newProductIds,
      product_variants: newProductVariantIds,
    };
  }

  return filters;
};

export const syncPromotions = (filters: FilterParams, event?: Event | null) => {
  const possibleTicketCategories = event?.ticket_categories || [];
  const possibleTicketCategoryIds = map(possibleTicketCategories, 'id');
  const newTicketCategoryIds = intersection((filters?.ticket_categories || []), possibleTicketCategoryIds);

  const possibleTickets = event?.ticket_categories.reduce(
    (tickets, ticketCategory) => (!newTicketCategoryIds.includes(ticketCategory.id)
      ? tickets
      : [...tickets, ...ticketCategory.tickets]),
    [],
  ) || [];
  const possibleTicketIds = map(possibleTickets, 'id');
  const newTicketIds = intersection((filters?.tickets || []), possibleTicketIds);

  const possibleProducts = event?.products || [];
  const possibleProductIds = map(possibleProducts, 'id');
  const newProductIds = intersection((filters?.products || []), possibleProductIds);

  const possiblePromotionIds = [
    ...map(flatten(map(possibleTickets.filter((ticket) => (
      (filters?.tickets || []).includes(ticket.id)
    )), 'promotions')), 'id'),
    ...map(flatten(map(possibleProducts.filter((product) => (
      (filters?.products || []).includes(product.id)
    )), 'promotions')), 'id'),
  ];

  const newPromotionIds = intersection((filters?.promotions || []), possiblePromotionIds);

  if (newTicketCategoryIds.join() !== (filters?.ticket_categories || []).join()
            || newTicketIds.join() !== (filters?.tickets || []).join()
            || newProductIds.join() !== (filters?.products || []).join()
            || newPromotionIds.join() !== (filters?.promotions || []).join()) {
    return {
      ...filters,
      ticket_categories: newTicketCategoryIds,
      tickets: newTicketIds,
      products: newProductIds,
      promotions: newPromotionIds,
    };
  }

  return filters;
};

export const syncParticipantFields = (filters: FilterParams, participantFields: Event['participant_fields']) => {
  const possibleFieldIds = map(participantFields, 'id');
  const selectedParticipantFieldIds = (filters?.participant_fields || []).map(({ id }) => id);
  const newFieldIds = intersection(selectedParticipantFieldIds, possibleFieldIds);

  const possibleChoiceIds = map(flatten(map(participantFields.filter((field) => (
    selectedParticipantFieldIds.includes(field.id)
  )), 'choices')), 'id');

  const newChoiceIds = intersection((filters?.participant_field_choices || []), possibleChoiceIds);

  if (newFieldIds.join() !== selectedParticipantFieldIds.join()
            || newChoiceIds.join() !== (filters?.participant_field_choices || []).join()) {
    return {
      ...filters,
      participant_fields: newFieldIds.map((id) => ({ id })),
      participant_field_choices: newChoiceIds,
    };
  }

  return filters;
};
